/// <reference types="vite/client" />

import { Environment } from './types';

const ENV = import.meta.env.VITE_APP_ENV as Environment || 'development';

export const APP_CONFIG = {
  // Environment configuration
  env: {
    current: ENV,
    isDevelopment: ENV === 'development',
    isProduction: ENV === 'production',
    isDemo: ENV === 'demo',
    isTest: ENV === 'test',
  },

  // API configuration
  api: {
    baseUrl: import.meta.env.VITE_API_URL,
    timeout: 30000,
    retryAttempts: 3,
    endpoints: {
      auth: {
        login: '/auth/login',
        register: '/auth/register',
        verify: '/auth/verify',
        refreshToken: '/auth/refresh',
      },
      sales: {
        list: '/sales',
        forecast: '/sales/forecast',
      },
      products: {
        list: '/products',
        create: '/products/create',
        update: '/products/update',
      },
      marketplaces: {
        list: '/marketplaces',
        connect: '/marketplaces/connect',
      },
      warehouses: {
        list: '/warehouses',
        detail: (id: string) => `/warehouses/${id}`,
        create: '/warehouses'
      }
    },
  },

  // Authentication configuration
  auth: {
    tokenKey: 'auth-token',
    companyIdKey: 'company-id',
    sessionTimeout: 3600, // in seconds
    refreshTokenBefore: 300, // refresh token 5 minutes before expiry
  },

  // UI/UX configuration
  ui: {
    theme: {
      colors: {
        primary: '#0084FF',
        primaryTint: "#F2F9FF",
        secondary: '#02066E',
        background: '#FFFFFF',
        backgroundTint: '#F8FBFF',
        border: '#E0EAF3',
        borderHover: '#AEC6DC',
        primaryText: '#040747',
        secondaryText: '#686A90',
        buttonContent: '#FFFFFF',
        error: '#EA2E2E',
        success: '#27CF56',
        warning: '#FC870A',
      },
      spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
      },
      breakpoints: {
        xs: '320px',
        sm: '600px',
        md: '960px',
        lg: '1280px',
        xl: '1920px',
      },
    },
    pagination: {
      defaultPageSize: 10,
      pageSizeOptions: [10, 25, 50, 100],
    },
    dateFormat: {
      default: 'dd/MM/yyyy',
      withTime: 'dd/MM/yyyy HH:mm',
      api: 'yyyy-MM-dd',
    },
  },

  // Feature flags
  features: {
    enableTutorial: true,
    enableMarketplaces: true,
    enableAnalytics: ENV === 'production',
    betaFeatures: ENV === 'development',
  },

  // Support / help widget
  support: {
    demoUrl: import.meta.env.VITE_DEMO_URL as string | undefined,
  },

  // Error handling configuration
  errors: {
    reporting: {
      enabled: ENV === 'production',
      sampleRate: 0.1, // report 10% of errors
      excludeFromForecastingErrors: ['NetworkError', 'AbortError'],
    },
    retry: {
      maxAttempts: 3,
      baseDelay: 1000, // milliseconds
      maxDelay: 5000, // milliseconds
    },
  },

  // Cache configuration
  cache: {
    ttl: {
      products: 5 * 60 * 1000, // 5 minutes
      sales: 15 * 60 * 1000, // 15 minutes
      marketplaces: 60 * 60 * 1000, // 1 hour
    },
    queryStaleTime: 5 * 60 * 1000, // 5 minutes
  },

  // Validation rules
  validation: {
    password: {
      minLength: 8,
      requireNumbers: true,
      requireSpecialChars: true,
      requireUppercase: true,
    },
    input: {
      maxLength: 255,
      textAreaMaxLength: 1000,
    },
  },
}; 